import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { RouteComponentProps, withRouter } from 'react-router'
import { urlTransform } from 'lib/string/removeSpaceUtils'
import HeadData from 'components/Common/HeadData'
import type { SearchFormRef } from 'components/Search'
import {
  getDestinationProductPage,
  getTitle,
  getRelatedTravelItems,
  decodeUrlParams,
  getSubheading,
  getMetaHeadTitle,
  getMetaDescription,
  isExperimentOn,
} from 'lib/offer/destinationProductPageUtils'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import HttpStatus from 'components/Common/HttpStatus'
import SimpleRelatedTravelItems from 'components/OfferPage/SimpleRelatedTravelItems'
import { fetchPlaceByName } from 'actions/PlaceActions'
import { fetchRelatedTravelDestinations } from 'actions/DestinationProductPageActions'
import Faq from 'components/Common/Faq'
import Breadcrumbs from 'components/Pages/DestinationProductPage/Breadcrumbs'
import SearchForm from 'components/Search/SearchForm'
import { isSearchEnabled, decodeSearchParams, getSearchKey } from 'lib/search/searchUtils'
import { isDynamicSearchFooterBoxContent } from 'selectors/featuresSelectors'
import DestinationPageRelatedTravelItems from './Support/DestinationPageRelatedTravelItems'
import TopDestinationList, { DestinationNavItemType } from 'components/Common/TopDestinationsList/TopDestinationList'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferList from 'components/OfferList/OfferList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffer from 'hooks/Offers/useOffer'
import DestinationOfferListAbTestProvider from 'contexts/OfferList/DestinationOfferListAbTestProvider'
import { ANYWHERE_PLACE_ID, HOTEL_SEARCH_TYPEAHEAD_TYPES, SEARCH_VERTICALS } from 'constants/search'
import LinkBoxes from 'components/Common/LinkBox/LinkBoxes'
import { fetchLinkBox } from 'actions/ContentActions'
import { getListName } from 'analytics/enhanced-ecommerce'
import {
  OfferListEventsProvider,
  OfferListEventHandler,
  OfferListEvents,
} from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import OfferCountAndSort from '../HotelSearchPage/OfferCountAndSort'
import { searchSortByTypes } from 'constants/offerListFilters'
import { mediaQueryUp } from '../../utils/breakpoint'
import HotelSearchPageFilterPane, { FilterControlChangeHandler } from '../HotelSearchPage/HotelSearchPageFilterPane'
import { defaultEnabledFilters, EnabledFilters } from '../../Search/type'
import { pushWithRegion, replaceSearchString } from 'actions/NavigationActions'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { HOTEL_SEARCH_TYPES, OFFER_TYPE_BED_BANK, OFFER_TYPE_BUNDLE_AND_SAVE } from 'constants/offer'
import { deleteSearchParams, parseSearchString } from 'lib/url/searchUrlUtils'
import SearchField from '../HotelSearchPage/SearchField'
import useGlobalSearchURLHashVertical from '../../../hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useQueryParams from '../../../hooks/useQueryParams'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import getOfferListKey from 'lib/offer/offerListKey'
import switchFunc from 'lib/function/switchFunc'
import DestinationProductPageHeader from './DestinationProductPageHeader'
import { searchEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import useSearchSession from 'hooks/Search/useSearchSession'
import FaqWrapper from '../SearchPage/FaqWrapper'
import { replace } from 'connected-react-router'
import { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'

const productTypeToSearchVertical = switchFunc({
  Hotels: SEARCH_VERTICALS.HOTELS,
  Tours: SEARCH_VERTICALS.TOURS,
}, SEARCH_VERTICALS.HOTELS)

const TopDestinationListContainer = styled(Pane)`
 padding: ${rem(16)} 0 ${rem(24)};
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }
`

const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

const DesktopContainer = styled.div`
  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(10)} ${rem(16)} ${rem(10)};
    border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
    margin-top: ${rem(16)};
  }
`

const StyledSearchField = styled(SearchField)`
  max-width: 100%;
  padding-top: ${rem(12)};
  padding-bottom: 0;
`

type MatchProps = {
  location?: string;
  holidayType?: string;
  productType: string;
  locationOrHolidayType?: string;
  country?:string;
  state?:string;
  city?:string;
}

interface Props extends RouteComponentProps<MatchProps> {
  regionCode: string;
  isSearchEnabled: boolean;
  locationToPlace: { [location: string]: App.Place }
  placesLoading: { [location: string]: boolean }
  customHeadings?: App.ContentHeadings;
  isDynamicSearchFooterBoxContent: boolean
  windowSearch: string,
  filtersFromUrl: App.OfferListFilters
}

function DestinationProductPage({
  regionCode,
  match,
  isSearchEnabled,
  locationToPlace,
  placesLoading,
  windowSearch,
  customHeadings,
  isDynamicSearchFooterBoxContent,
  filtersFromUrl,
}: Props) {
  const { url, params } = match
  const {
    location,
    holidayType,
    productType,
    shouldRedirect,
  } = decodeUrlParams(params)

  const formRef = useRef<SearchFormRef>(null)

  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  useEffect(() => {
    if (productType === 'Hotels') {
      if (location) {
        dispatch(fetchRelatedTravelDestinations(location))
      }
      dispatch(fetchLinkBox())
    }
  }, [productType, location, dispatch])

  const onBedbankSelectDatesPrompt = useCallback(() => {
    formRef.current?.form.current.scrollIntoView()
    formRef.current?.openDatePickerMenu()
  }, [])

  const parsedSearchParams = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const decodedSearchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const searchKey = getSearchKey(parsedSearchParams)
  const { searchId } = useSearchSession(searchKey)
  const place = location ? locationToPlace[location] : undefined
  const placeLoading = location ? placesLoading[location] : undefined
  const placeId = place?.id
  const canonicalName = place?.canonicalName
  const searchFormEnabled = isSearchEnabled && productType !== 'Tours-&-Cruises'
  const searchVertical = productTypeToSearchVertical(productType)
  const searchItemPlaceId = decodedSearchParams?.searchItem?.value || placeId || ''

  const searchItem = useMemo<App.SearchItem>(() => (
    {
      searchType: 'destination',
      value: searchItemPlaceId,
      placeId: searchItemPlaceId,
      format: ({
        mainText: decodedSearchParams?.searchItem?.format.mainText || place?.name || '',
        secondaryText: decodedSearchParams?.searchItem?.format.secondaryText || '',
      }),
    }), [place?.name, decodedSearchParams?.searchItem, searchItemPlaceId])
  const isExperiment = isExperimentOn(url, productType)

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType: 'destination',
    searchItem,
    onBedbankSelectDatesPrompt,
    searchTargetLabel: place?.name,
  }, queryParams)

  const parseCanonicalName = (canonicalName:string) => {
    const splittedCanonicalName = canonicalName.split(', ')
    return (splittedCanonicalName.length === 1 ? 'country' : 'location') + '/' + splittedCanonicalName.join('/')
  }

  useEffect(() => {
    if (!place && location) {
      dispatch(fetchPlaceByName(location))
    }
  }, [dispatch, location, place])

  useEffect(() => {
    if (productType === 'Hotels' && place) {
      // we need to append these to the search string to ensure that filters work
      dispatch(replaceSearchString({ destinationName: place.name, destinationId: place.id, adults: 2, children: 'none', flexibleWithDates: true }))
    }
  }, [place, dispatch, productType])

  const types = getDestinationProductPage(location, holidayType, productType).pageTypes

  const filters = useMemo((): App.OfferListFilters => {
    if (productType === 'Hotels') {
      return {
        ...filtersFromUrl,
        offerTypes: [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK],
        distanceEq: 0,
        isLight: true,
      }
    }

    return {
      holidayTypes: getDestinationProductPage(location, holidayType, productType)?.holidayTypes,
      destinationId: location ? placeId : undefined,
      distanceEq: 0,
      useUnifiedSearchEndpoint: true,
      offerTypes: types,
    }
  }, [productType, location, filtersFromUrl, holidayType, placeId, types])

  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      eventAnalytics: { searchId },
      searchVerticals: new Set([searchVertical]),
      searchItem,
    })
  }, [globalHotelsSearchState, searchId, searchVertical, searchItem])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'Destination Product', 'Main Results'),
    {
      ga: {
        category: 'OfferList',
        label: 'Main Results',
      },
      key: getOfferListKey(filters),
    },
  )

  const offerList = useOfferList(filters, { noFetch: true })
  const destinationProductListKey = offerList.key
  const [offer] = useOffer(offerList.offerIds[0])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    if (productType === 'Hotels') { // If hotel search send user to search page when filter selected
      dispatch(pushWithRegion('/search', params.search, undefined, params.hash))
      return
    }

    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch, productType])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    if (productType === 'Hotels') { // If hotel search send user to search page when sort selected
      dispatch(pushWithRegion('/search', params.search))
      return
    }

    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch, productType])

  const isMobileScreen = useScreenSizeOnly('mobile')
  const isDesktop = useScreenSizeUp('largeDesktop')
  const enabledFilters: EnabledFilters = { ...defaultEnabledFilters, priceFilter: false }
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const openSearchMenu = useCallback(() => { toggleSearchVertical(searchVertical) }, [toggleSearchVertical, searchVertical])
  const showFilterPane = isDesktop && (!!offerList.offerCount || offerList.fetching)
  const canonicalNameRedirect = productType === 'Hotels' && canonicalName && canonicalName.toLowerCase() !== location
  const hotelHolidayTypeRedirect = productType === 'Hotels' && location && (shouldRedirect || holidayType)
  const shouldStreamSearchEvents = ['Hotels', 'Tours', 'Holiday Packages'].includes(productType) && !shouldRedirect && !placeLoading && !canonicalNameRedirect && !hotelHolidayTypeRedirect && place && searchContext.destinations && searchContext.destinations.length > 0 && (filters.destinationId || filters.destinations && filters.destinations.length > 0)
  const trackingOptions = useMemo(() => shouldStreamSearchEvents ? ({ searchContext }) : undefined, [searchContext, shouldStreamSearchEvents])
  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad && destinationProductListKey === dispatchAction.key && shouldStreamSearchEvents) {
      Analytics.trackEvent(searchEvent('offer-list-load', `destination-${searchVertical}-product-page`, {
        filters,
        searchItem: decodedSearchParams.searchItem,
        vertical: searchVertical,
      }))
    }
  }, [destinationProductListKey, filters, decodedSearchParams.searchItem, searchVertical, shouldStreamSearchEvents])

  if (canonicalNameRedirect) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(canonicalName).toLowerCase())}`}/>
  }

  if (hotelHolidayTypeRedirect) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(location.toLowerCase()))}`}/>
  }

  if (shouldRedirect) {
    if (productType === 'Hotels' && location) {
      return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(location.toLowerCase()))}`}/>
    }
    // These can be removed once we have ported all the product types to the above
    // https://github.com/lux-group/svc-public-offer-feed/blob/ad20cba1c56afb21b630d85b9ff4472f64c9ed7e/src/context/destinationProductPages.js#L10437-L10442
    if (match.params.locationOrHolidayType !== undefined) {
      return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/${match.params.locationOrHolidayType.toLowerCase()}.${match.params.productType?.toLowerCase()}`} />
    }
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/${match.params.location?.toLowerCase()}.${match.params.holidayType?.toLowerCase()}.${match.params.productType?.toLowerCase()}`} />
  }

  if (place === null || productType === undefined || (location === undefined && holidayType === undefined)) {
    return <HttpStatus code={404} ><Error404PageWithOffers /></HttpStatus>
  }

  const headTitle = getMetaHeadTitle(regionCode, location, holidayType, productType, canonicalName, isExperiment)
  const title = customHeadings?.heading ?? getTitle(location, holidayType, productType, canonicalName)
  const metaDescription = getMetaDescription(location, holidayType, productType, canonicalName, isExperiment)
  const relatedTravelItems = getRelatedTravelItems(location, holidayType, productType, canonicalName)
  const subheading = customHeadings?.subHeading ?? getSubheading(location, productType, canonicalName)
  const isCountry = !match.params.city && match.params.country
  const isCity = match.params.city

  if (!place) {
    filters.destinationId = ANYWHERE_PLACE_ID
  }

  let navItems: Array<DestinationNavItemType> = []
  if (isCountry) navItems = ['Countries', 'Popular', 'Cities']
  if (isCity) navItems = ['Cities', 'Popular', 'Countries']

  return <GlobalSearchContextProvider
    state={globalHotelsSearchState}
    dispatch={globalHotelsSearchDispatch}
  >
    <HeadData
      title={headTitle}
      description={metaDescription}
      canonical={productType.toLowerCase() === 'hotels' && location ? `/${productType.toLowerCase()}/${urlTransform(parseCanonicalName(location))}` : undefined}
      imageId={offer && 'images' in offer && offer.images[0] ? offer.images[0].id : undefined}
    />
    <Breadcrumbs location={location} holidayType={holidayType} productType={productType} regionCode={regionCode} />
    <DestinationProductPageHeader
      title={title}
      subheading={subheading}
    />
    {searchFormEnabled && (
      <>
        <DesktopContainer>
          <SearchForm
            submitOnApply
            filters={filters}
            ref={formRef}
            showDateFieldOnMobile={false}
            typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
          />
        </DesktopContainer>
        {isMobileScreen && (
          <StyledSearchField
            filters={filters}
            sortOptions={searchSortByTypes}
            onEdit={openSearchMenu}
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
          />
        )}
      </>
    )}
    {!placeLoading && (
      <OfferListEventsProvider tracking={tracking}
        onListEvent={onListEvent}
      >
        <DestinationOfferListAbTestProvider sourceType="destination-product">
          <StyledLayoutContainer size={showFilterPane ? 'xl' : 'lg'} className={cn({ filterPane: showFilterPane, fullWidth: true })}>
            <StyledVerticalSpacer gap={16}>
              <OfferList
                filters={filters}
                tileStyle="search"
                fullWidth={isMobileScreen}
                interstitials={[
                  {
                    id: 'no-offers',
                    element: <BodyText variant="large" as="h2" align="center">No offers available</BodyText>,
                    show: 'hasNoOffers',
                    position: 'start',
                  },
                  {
                    id: 'heading',
                    position: 'start',
                    element: <OfferCountAndSort
                      windowSearch={windowSearch}
                      mainListTotal={offerList.offerCount!}
                      sortOptions={searchSortByTypes}
                      filters={filters}
                      destinationName={place?.name}
                      mapViewUrl={mapViewURL}
                      enabledFilters={enabledFilters}
                      shouldShowFilters={!showFilterPane}
                      fetching={offerList.fetching}
                      searchType="destination"
                      onFilterChange={handleFilterChange}
                      onSortChange={handleSortChange}
                    />,
                    show: !offerList.fetching && productType === 'Hotels',
                  },
                ]}
                trackingOptions={trackingOptions}
              />
            </StyledVerticalSpacer>
            {showFilterPane && (
              <StyledHotelSearchPageFilterPane
                filters={filters}
                mapViewURL={mapViewURL}
                search={windowSearch}
                enabledFilters={enabledFilters}
                onFilterChange={handleFilterChange}
              />
            )}
          </StyledLayoutContainer>
        </DestinationOfferListAbTestProvider>
      </OfferListEventsProvider>
    )}
    {!isDynamicSearchFooterBoxContent && productType !== 'Hotels' && relatedTravelItems.length > 0 && <SimpleRelatedTravelItems items={relatedTravelItems} />}
    {!isDynamicSearchFooterBoxContent && productType === 'Hotels' && location && navItems.length > 0 && <TopDestinationListContainer>
      <PaneBody>
        <TopDestinationList destinationNavItems={navItems}/>
      </PaneBody>
    </TopDestinationListContainer>}
    {isDynamicSearchFooterBoxContent && <LinkBoxes />}
    {location && <Faq
      type="destinationProduct"
      maxToShow={10}
      slug={`${urlTransform(location).toLowerCase()}`}
      tags={[productType]}
      wrapper={FaqWrapper}
    />}
    {productType === 'Hotels' && relatedTravelItems.length > 1 && <DestinationPageRelatedTravelItems items={relatedTravelItems} />}
  </GlobalSearchContextProvider>
}

const mapStateToProps = (state: App.State) => ({
  locationToPlace: state.destination.locationToPlace,
  placesLoading: state.destination.placesLoading,
  regionCode: state.geo.currentRegionCode,
  isSearchEnabled: isSearchEnabled(),
  customHeadings: state.content?.secureHeadingParams?.landing,
  isDynamicSearchFooterBoxContent: isDynamicSearchFooterBoxContent(state),
  windowSearch: state.router.location.search,
  filtersFromUrl: getUrlOfferListFilters(state),
})

export default withRouter(connect(mapStateToProps)(DestinationProductPage))
